import type { DefaultValues } from 'react-hook-form';
import {
  toModifierSelection,
  type MenuCategory,
  type MenuModifierGroup,
  type MenuProduct,
  type ProductInput,
} from '@/entities/menu';
import { isKnownUnitCode } from '../config/unit-codes';
import type {
  ModifierGroupFormValues,
  ModifierGroupValues,
  ProductFormImage,
  ProductFormValues,
} from '../model/schema';

/** A merchant branch shown in the drawer's branch multi-select. */
export interface Branch {
  id: string;
  name: string;
}

/**
 * Map an existing product (or a pre-selected category) to form defaults.
 *
 * The catalog's read endpoints resolve `name`/`description` to one locale, so
 * an existing product usually has no `*Translations`. Russian therefore falls
 * back to the resolved string — it is the backend's own canonical fallback, so
 * what the merchant sees is what the storefront shows — while Uz/En start
 * blank rather than echoing a Russian value into the wrong language field.
 */
export function buildDefaults(
  product: MenuProduct | undefined,
  defaultCategoryId: MenuCategory['id'] | undefined,
): DefaultValues<ProductFormValues> {
  return {
    nameRu:
      product?.nameTranslations?.ru === ''
        ? (product.name ?? '')
        : (product?.nameTranslations?.ru ?? product?.name ?? ''),
    nameUz: product?.nameTranslations?.uz ?? '',
    nameEn: product?.nameTranslations?.en ?? '',
    descriptionRu:
      product?.descriptionTranslations?.ru === ''
        ? (product.description ?? '')
        : (product?.descriptionTranslations?.ru ?? product?.description ?? ''),
    descriptionUz: product?.descriptionTranslations?.uz ?? '',
    descriptionEn: product?.descriptionTranslations?.en ?? '',
    compositionRu: product?.compositionTranslations?.ru ?? '',
    compositionUz: product?.compositionTranslations?.uz ?? '',
    compositionEn: product?.compositionTranslations?.en ?? '',
    ...(product?.nutrition?.calories == null
      ? {}
      : { calories: product.nutrition.calories }),
    ...(product?.nutrition?.fats == null ? {} : { fats: product.nutrition.fats }),
    ...(product?.nutrition?.proteins == null
      ? {}
      : { proteins: product.nutrition.proteins }),
    // No `branchIds`: the editor does not choose branches. Product create no
    // longer sends branch overlays in the POST body, and edit is scoped by
    // `menuApi.updateProduct`. Seeding a value here would put it in the form's
    // dirty state for no one to edit.
    categoryId: String(product?.categoryId ?? defaultCategoryId ?? ''),
    alwaysReady: product?.alwaysReady ?? false,
    isAdult: product?.isAdult ?? false,
    modifiersDisabled: product?.modifiersDisabled ?? false,
    // Seconds on the wire → whole minutes in the form. Left undefined (an empty
    // input) rather than 0, so an existing product with no stored cook time
    // shows the required field blank instead of a bogus "0".
    ...(product?.cookTimeSeconds !== undefined && product.cookTimeSeconds > 0
      ? { prepTimeMinutes: Math.round(product.cookTimeSeconds / 60) }
      : {}),
    // Already-saved photos are seeded from their display URLs and flagged
    // `saved` — they carry no `file`, and their URL is an expansion of the
    // storage key rather than the key itself, so they must render but never be
    // written back. See `toProductInput`.
    images: buildSavedImages(product),
    ...(product ? { price: product.price } : {}),
    ikpuCode: product?.ikpuCode ?? '',
    unitCode: isKnownUnitCode(product?.unitCode) ? product.unitCode : '5',
    ...(product?.measureAmount ? { measureAmount: product.measureAmount } : {}),
    measurementType: product?.measurementType ?? 'none',
    ...(product?.vatPercent === undefined ? {} : { vatPercent: product.vatPercent }),
    ...(product?.availableFrom ? { availableFrom: product.availableFrom } : {}),
    ...(product?.availableTo ? { availableTo: product.availableTo } : {}),
    hasMarkedCode: product?.hasMarkedCode ?? false,
    ...(product?.markedCode === undefined ? {} : { markedCode: product.markedCode }),
    modifiers: buildModifierDefaults(product),
  };
}

/**
 * The modifier step's rows for an existing product.
 *
 * Every name arrives as a full translation set (API v1.00.180), so all three
 * inputs seed from stored values rather than Ru-only. A missing uz/en is a
 * genuine "not translated yet" and seeds blank — never echoed from Ru, which
 * would present a fallback as a real translation.
 */
function buildModifierDefaults(
  product: MenuProduct | undefined,
): ModifierGroupFormValues[] {
  return (product?.modifiers ?? []).map((group) => ({
    // Round-tripped so a save UPDATES this group rather than creating a
    // duplicate alongside it.
    ...(group.id === undefined ? {} : { id: group.id }),
    nameRu: group.name.ru,
    nameUz: group.name.uz ?? '',
    nameEn: group.name.en ?? '',
    // The choice controls derive from the wire's min/max pair (v1.00.227):
    // a cap of exactly one is the radio case; anything else is checkboxes,
    // with the cap box seeded from the stored ceiling (blank = no ceiling).
    choiceType: toModifierSelection(group.maxSelect),
    required: group.minSelect >= 1,
    // The RAW stored minimum rides along unrendered — see the schema comment:
    // the toggle alone cannot round-trip a provider's min == 8.
    minSelect: group.minSelect,
    ...(group.maxSelect !== undefined && group.maxSelect !== 1
      ? { maxSelect: group.maxSelect }
      : {}),
    options: group.options.map((option) => ({
      ...(option.id === undefined ? {} : { id: option.id }),
      nameRu: option.name.ru,
      nameUz: option.name.uz ?? '',
      nameEn: option.name.en ?? '',
      priceDelta: option.priceDelta,
      ikpuCode: option.ikpuCode ?? '',
      unitCode: option.unitCode ?? '',
      ...(option.vatPercent === undefined ? {} : { vatPercent: option.vatPercent }),
    })),
  }));
}

/**
 * The gallery entries for a product's already-stored photos.
 *
 * Prefers the full `imageUrls` gallery (present on the by-id read that seeds
 * the editor) and falls back to the lone `imageUrl` the catalog card carries,
 * so the drawer still shows a cover when opened before the read resolves.
 */
function buildSavedImages(product: MenuProduct | undefined): ProductFormImage[] {
  const urls = product?.imageUrls?.length
    ? product.imageUrls
    : product?.imageUrl
      ? [product.imageUrl]
      : [];

  // The cover is whichever entry the wire typed `main`, NOT simply the
  // first — `position` and `image_type` are independent, so a merchant who starred
  // the third photo without reordering would otherwise see the first one shown
  // as cover, and saving would move their choice to match.
  const coverIndex = product?.coverIndex ?? 0;

  return urls.map((url, index) => ({
    // The SERVER's image id when we have one, so a submit can name exactly
    // which stored photos survived (`keptImageIds`). Falls back to a synthetic
    // id for the catalog-card seed, which carries a cover URL but no gallery
    // ids — such an entry is display-only until the by-id read resolves.
    id: product?.imageIds?.[index] ?? `existing-${index}`,
    url,
    saved: true,
    isPrimary: index === coverIndex,
  }));
}

/**
 * The selling-hours part of the input. Three outcomes, mirroring the wire's
 * "absent / null / object":
 *
 * - unchanged against the stored hours → nothing (`availability_window` stays
 *   off the request, so a save that never touched the pair rewrites nothing);
 * - emptied while the dish had hours → `null` (sold all day again);
 * - otherwise the new pair.
 *
 * A create has no stored hours, so an empty pair is "unchanged" there.
 */
function toAvailabilityWindowInput(
  values: Pick<ProductFormValues, 'availableFrom' | 'availableTo'>,
  saved: { from?: string | undefined; to?: string | undefined },
): Pick<ProductInput, 'availabilityWindow'> {
  const from = values.availableFrom ?? undefined;
  const to = values.availableTo ?? undefined;
  if (from === (saved.from ?? undefined) && to === (saved.to ?? undefined)) return {};
  if (from === undefined && to === undefined) return { availabilityWindow: null };
  return {
    availabilityWindow: {
      ...(from === undefined ? {} : { from }),
      ...(to === undefined ? {} : { to }),
    },
  };
}

/**
 * Assemble the wire input from the validated form + the resolved gallery.
 *
 * `images` here are post-upload: freshly-picked entries have had their `blob:`
 * preview swapped for a storage KEY, while already-saved entries still hold the
 * display URL the read expanded from their key.
 *
 * Only keys are ever sent. Saved entries contribute their server IDS instead
 * (`keptImageIds`) — that is what tells the API layer to leave those photos in
 * place rather than deleting them, which it could not undo (a stored photo's
 * URL is a one-way expansion of its key, so it cannot be re-uploaded).
 *
 * The gallery is written only when something actually changed: a new photo, or
 * a removal. An untouched gallery leaves `imageKeys` undefined and the API
 * layer touches no image endpoint.
 */
export function toProductInput(
  values: ProductFormValues,
  images: ProductFormImage[],
  /** Whether the product being edited already had photos stored. */
  hadSavedImages = false,
  /** How many photos the product had before this edit, to detect removals. */
  savedImageCount = 0,
  /**
   * Which stored photo was the cover before this edit, by its gallery ID.
   *
   * An ID rather than an index, because the grid REORDERS on promote — see the
   * `movedCover` comment below for why an index could not detect the change.
   */
  savedCoverId?: string,
  /**
   * The stored modifiers switch. The switch writes through its own endpoint,
   * so it is sent only when the merchant actually flipped it — a create
   * compares against `false`, i.e. sends it only when turned on.
   */
  savedModifiersDisabled = false,
  /** The dish's stored selling hours, to tell an edit from an untouched pair. */
  savedHours: { from?: string | undefined; to?: string | undefined } = {},
  savedProduct?: MenuProduct,
): ProductInput {
  const modifiersDisabled = values.modifiersDisabled ?? false;
  // A saved entry's `url` is an expanded display URL, never a key — dropping
  // them here is what keeps today's bucket host out of the stored row.
  const newImages = images.filter((image) => !image.saved);
  const keys = newImages.map((image) => image.url);
  const savedImages = images.filter((image) => image.saved);
  const keptImageIds = savedImages.map((image) => image.id);
  const isCleared = hadSavedImages && images.length === 0;
  // A removal is invisible in `keys` (nothing was added), so it is detected by
  // the survivor count falling short of what was stored.
  const hasRemoval = keptImageIds.length < savedImageCount;
  // Starring a different already-stored photo changes neither list, so without
  // this a cover-only edit wrote nothing at all and the star silently reverted
  // on the next read.
  //
  // The comparison is by IDENTITY, not position. `FormField.ImageGrid` promotes
  // a photo by SWAPPING it into slot 0 (`ensurePrimary` types whatever sits
  // there as the cover), so the starred entry's index is always 0 — and against
  // a product whose stored cover was also index 0, which is the overwhelmingly
  // common case, an index comparison read "unchanged" and skipped the write
  // entirely. Picking a different existing photo as the main one therefore did
  // nothing at all, and the catalog card kept showing the previous photo.
  //
  // Whichever entry NOW carries the star is compared against whichever one held
  // it when the form was seeded (`buildSavedImages` flags `savedCoverIndex`),
  // so a swap is detected however the grid chose to reorder around it.
  const coverEntry = images.find((image) => image.isPrimary);
  // With no `savedCoverId` there is nothing to compare against — a create, or
  // an edit seeded from a catalog card whose gallery ids the read has not
  // delivered yet. "Unknown" must read as "unchanged", not as a move: writing
  // on a guess would rewrite the cover of a product the merchant never touched.
  const movedCover =
    coverEntry !== undefined &&
    savedCoverId !== undefined &&
    coverEntry.id !== savedCoverId;
  const writesGallery = keys.length > 0 || isCleared || hasRemoval || movedCover;

  // Name the cover the only way each kind of photo CAN be named: a stored one
  // by its server id, a fresh one by its index in `imageKeys` (its id does not
  // exist until the add lands). Deriving it from the two lists is what used to
  // fail — they concatenate kept-before-new, so a new cover never won.
  const cover: ProductInput['cover'] =
    coverEntry === undefined
      ? undefined
      : coverEntry.saved
        ? { kind: 'kept', id: coverEntry.id }
        : { kind: 'new', index: newImages.indexOf(coverEntry) };

  return {
    ...(writesGallery
      ? { imageKeys: keys, keptImageIds, ...(cover === undefined ? {} : { cover }) }
      : {}),
    categoryId: values.categoryId,
    // Required on create (schema-enforced); omitted on an edit that left it blank.
    ...(values.price === undefined ? {} : { price: values.price }),
    // Every language is required by the schema, so both translation sets are
    // always complete — no member can be blank by the time we get here.
    nameTranslations: {
      ru: values.nameRu,
      uz: values.nameUz,
      en: values.nameEn,
    },
    descriptionTranslations: {
      ru: values.descriptionRu,
      uz: values.descriptionUz,
      en: values.descriptionEn,
    },
    ...toCompositionPatch(values, savedProduct),
    ...toNutritionPatch(values, savedProduct),
    ...(values.branchIds === undefined ? {} : { branchIds: values.branchIds }),
    // The form collects whole minutes; the wire field is `cook_time_seconds`.
    // An always-ready product carries no cook time at all.
    alwaysReady: values.alwaysReady ?? false,
    isAdult: values.isAdult ?? false,
    ...(modifiersDisabled === savedModifiersDisabled ? {} : { modifiersDisabled }),
    ...(values.alwaysReady !== true && values.prepTimeMinutes !== undefined
      ? { cookTimeSeconds: values.prepTimeMinutes * 60 }
      : {}),
    ikpuCode: values.ikpuCode,
    unitCode: values.unitCode,
    ...(values.measureAmount === undefined
      ? {}
      : { measureAmount: values.measureAmount }),
    ...(values.vatPercent === undefined ? {} : { vatPercent: values.vatPercent }),
    ...toAvailabilityWindowInput(values, savedHours),
    ...(values.measurementType === 'none'
      ? {}
      : { measurementType: values.measurementType }),
    hasMarkedCode: values.hasMarkedCode ?? false,
    ...(values.markedCode === undefined ? {} : { markedCode: values.markedCode }),
    // ALWAYS sent, including as an empty array, and that is deliberate: the
    // wizard seeds step 3 from the stored groups and always renders it, so the
    // submitted array is the merchant's actual intent rather than an artefact of
    // which steps they visited. Sending it is what makes "delete a group" (and
    // "delete the last group") reach the wire at all — the API layer reads a
    // present-but-empty `modifiers` as "clear", which is exactly right here.
    //
    // The "don't touch" case (`undefined`) is reserved for callers that never
    // collected modifiers; `menu-api` keeps supporting it so a future partial
    // editor cannot silently wipe them.
    modifiers: values.modifiers.map(toModifierGroupInput),
  };
}

function toCompositionPatch(
  values: ProductFormValues,
  saved?: MenuProduct,
): Pick<ProductInput, 'compositionTranslations'> {
  const current = {
    ru: values.compositionRu?.trim() ?? '',
    uz: values.compositionUz?.trim() ?? '',
    en: values.compositionEn?.trim() ?? '',
  };
  const previous = saved?.compositionTranslations;
  if (
    Object.entries(current).every(
      ([lang, value]) => value === (previous?.[lang as keyof typeof current] ?? ''),
    )
  )
    return {};
  return {
    compositionTranslations: Object.values(current).some(Boolean) ? current : null,
  };
}

function toNutritionPatch(
  values: ProductFormValues,
  saved?: MenuProduct,
): Pick<ProductInput, 'nutrition'> {
  const current = {
    calories: values.calories,
    fats: values.fats,
    proteins: values.proteins,
  };
  const previous = saved?.nutrition;
  const changed = Object.fromEntries(
    (Object.keys(current) as (keyof typeof current)[])
      .filter((key) => current[key] !== (previous?.[key] ?? undefined))
      .map((key) => [key, current[key] ?? null]),
  );
  if (Object.keys(changed).length === 0) return {};
  return {
    nutrition: Object.values(current).every((value) => value === undefined)
      ? null
      : changed,
  };
}

/**
 * A form modifier group → the entity's input shape.
 *
 * The choice controls fold back into the wire's `min_select`/`max_select` pair
 * (restored in API v1.00.227):
 *
 * - `single` → a cap of exactly 1; `multi` → the merchant's own cap, or no
 *   ceiling when the «3шт» box is blank.
 * - «Обязательно» → a minimum of at least 1 — but a STORED minimum above one
 *   (a provider range like min == 8) survives an untouched toggle, which is
 *   what the hidden `minSelect` field is for. Only flipping the toggle off
 *   collapses it to 0.
 */
function toModifierGroupInput(group: ModifierGroupValues): MenuModifierGroup {
  const storedMin = group.minSelect ?? 0;
  const minSelect = group.required === true ? Math.max(1, storedMin) : 0;
  const maxSelect = group.choiceType === 'single' ? 1 : group.maxSelect;
  return {
    ...(group.id === undefined ? {} : { id: group.id }),
    name: toModifierName(group.nameRu, group.nameUz, group.nameEn),
    minSelect,
    ...(maxSelect === undefined ? {} : { maxSelect }),
    options: group.options.map((option) => ({
      ...(option.id === undefined ? {} : { id: option.id }),
      name: toModifierName(option.nameRu, option.nameUz, option.nameEn),
      priceDelta: option.priceDelta,
      ...(option.ikpuCode ? { ikpuCode: option.ikpuCode } : {}),
      ...(option.unitCode ? { unitCode: option.unitCode } : {}),
      ...(option.vatPercent === undefined ? {} : { vatPercent: option.vatPercent }),
    })),
  };
}

/**
 * A modifier name's three inputs → the view model's translation set.
 *
 * Blank uz/en members are DROPPED rather than carried as empty strings: the
 * backend reads `''` as "not provided" and leaves the stored translation alone,
 * so an omitted member and an empty one mean the same thing on the wire — but
 * dropping them keeps the view model honest about what is actually translated.
 */
function toModifierName(
  ru: string,
  uz: string | undefined,
  en: string | undefined,
): MenuModifierGroup['name'] {
  return {
    ru,
    ...(uz?.trim() ? { uz: uz.trim() } : {}),
    ...(en?.trim() ? { en: en.trim() } : {}),
  };
}
